import { useCallback, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';
import { useEcho } from '@laravel/echo-react';
import { useUploadStore } from '@/hooks/data/inspector/useUploadStore';
import { TaskState, UploadFinishedPercentage } from '@/types/uploads';
import { QueryKey } from '@/lib/queryKeys';

export const AssetVersionCreatedListener = ({
  assetId,
  versionId,
  uploadSessionId,
}: {
  assetId: string;
  versionId?: string;
  uploadSessionId: string;
}) => {
  const queryClient = useQueryClient();
  const handledRef = useRef(false);

  const { setAssetsUploadProgress, setAverageProgress, setUploadStatus, setUploadState, setUploadField } =
    useUploadStore(
      useShallow((state) => ({
        setAssetsUploadProgress: state.setAssetsUploadProgress,
        setAverageProgress: state.setAverageProgress,
        setUploadStatus: state.setUploadStatus,
        setUploadState: state.setUploadState,
        setUploadField: state.setUploadField,
      })),
    );

  const channelName = useMemo(() => `asset.${assetId}`, [assetId]);

  const handleVersionCreated = useCallback(
    (event: { assetId?: string; versionId?: string }) => {
      if (handledRef.current || event?.assetId !== assetId || (versionId && event.versionId !== versionId)) {
        return;
      }

      const session = useUploadStore.getState().uploads[uploadSessionId];
      if (!session) {
        handledRef.current = true;
        return;
      }

      const fileIds = Object.values(session.filesByFolder)
        .flat()
        .map((file) => file.id);

      if (fileIds.length > 0) {
        fileIds.forEach((fileId) => setAssetsUploadProgress(uploadSessionId, fileId, UploadFinishedPercentage, true));
      }

      setAverageProgress(uploadSessionId, UploadFinishedPercentage);
      setUploadStatus(uploadSessionId, 'success');
      setUploadState(uploadSessionId, TaskState.Finished);
      setUploadField(uploadSessionId, 'completedUploadCount', session.totalAssetsToUpload || fileIds.length || 1);
      setUploadField(uploadSessionId, 'versionUploadAssetId', undefined);
      setUploadField(uploadSessionId, 'versionUploadVersionId', undefined);

      void queryClient.invalidateQueries({ queryKey: [QueryKey.Asset, assetId] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.AssetData] });

      handledRef.current = true;
    },
    [
      assetId,
      queryClient,
      setAssetsUploadProgress,
      setAverageProgress,
      setUploadField,
      setUploadState,
      setUploadStatus,
      uploadSessionId,
      versionId,
    ],
  );

  useEcho(channelName, '.asset.version.created', handleVersionCreated, [channelName, handleVersionCreated]);

  return null;
};
